import { VNode, RefObject } from 'preact';
import { createPortal, useContext, useLayoutEffect, useMemo, useState } from 'preact/compat';
import { LayerContext } from './LayerContext';
import { useThemeInterpolations } from '../hooks/UNSAFE_useThemeInterpolations/useThemeInterpolations';

const _LOGICAL_PARENT = '__oj_logical_parent';

type Props = {
  /**
   * The content to be re-parented using Portal
   */
  children: VNode;
  /**
   * Logical parent of the re-parented content. Typically the 'launcher' associated
   * with the popup.
   */
  logicalParentRef?: RefObject<Element> | null;
};

type LayerElement = HTMLDivElement & {
  __oj_logical_parent?: Element | null;
};

const Layer = (props: Props) => {
  const context = useContext(LayerContext);
  const defaultHost = context.getHost?.();
  const host = useMemo(() => defaultHost, [defaultHost]);
  const [layerContainer, setLayerContainer] = useState<Element | null>(null);

  // because the children are being rendered into a portal that is out of the flow of parent dom,
  // we need to specify the colorScheme and scale class names here to pick up the correct CSS styles.
  const classes = useThemeInterpolations();

  // Really need useLayoutEffect instead of useEffect here. Otherwise the re-parented
  // content does not get re-rendered with valid ref's
  useLayoutEffect(() => {
    if (!host) return;
    const doc = host.ownerDocument ?? document;
    const layer: LayerElement = doc.createElement('div');

    if (props.logicalParentRef) {
      layer[_LOGICAL_PARENT] = props.logicalParentRef.current;
    }

    host.appendChild(layer);
    setLayerContainer(layer);

    return () => {
      if (host && layer && host.contains(layer)) {
        delete layer[_LOGICAL_PARENT];
        host.removeChild(layer);
      }
      setLayerContainer(null);
    };
  }, [host]);

  // add the colorScheme and scale classes if there is a layerContainer
  if (layerContainer) {
    // use the theme interpolations to set the appropriate style classes for colorScheme and scale
    layerContainer.className = classes;
  }

  return layerContainer && createPortal(props.children, layerContainer);
};

export default Layer;
